import type { CanvasGroup, CanvasGroupGeometry, CanvasGroupSlice, CanvasGroupDeadZone } from './types';

interface MonitorDef {
  id: string; name: string;
  w: number; h: number;
  bt: number; br: number; bb: number; bl: number;
  nw: number; nh: number;
}

interface WallDef {
  id: string;
  monitors: MonitorDef[];
}

/**
 * Compute the virtual strip layout for a canvas group.
 *
 * Monitors are laid out left-to-right in the order specified by the group.
 * Between adjacent monitors, a gap equal to (right bezel of left monitor +
 * left bezel of right monitor) is inserted as a dead zone.
 *
 * The strip includes these gap pixels so that generated video content
 * accounts for the physical space hidden behind bezels.
 *
 * Render resolution uses the maximum pixel density across all monitors so
 * the highest-res monitor gets 1:1 output and others are upscaled.
 */
export function computeCanvasGroupGeometry(
  group: CanvasGroup,
  walls: WallDef[],
): CanvasGroupGeometry | null {
  const resolved: { mon: MonitorDef; ref: typeof group.monitors[0] }[] = [];

  for (const ref of group.monitors) {
    const wall = walls.find((w) => w.id === ref.wallId);
    if (!wall) continue;
    const mon = wall.monitors.find((m) => m.id === ref.monitorId);
    if (!mon) continue;
    resolved.push({ mon, ref });
  }

  if (resolved.length === 0) return null;

  // Max pixel density (px/mm) — render at the sharpest monitor's density
  const density = Math.max(...resolved.map(({ mon }) => Math.max(mon.nw / mon.w, mon.nh / mon.h)));

  // Physical dimensions including gaps
  const maxHeightMm = Math.max(...resolved.map(({ mon }) => mon.h));
  let totalWidthMm = 0;

  // Build per-monitor slices + dead zones
  let xCursor = 0;
  const slices: CanvasGroupSlice[] = [];
  const deadZones: CanvasGroupDeadZone[] = [];

  for (let i = 0; i < resolved.length; i++) {
    const { mon, ref } = resolved[i];

    // Insert gap before this monitor (bezel of previous + bezel of this)
    if (i > 0) {
      const prevMon = resolved[i - 1].mon;
      const gapMm = prevMon.br + mon.bl;
      const gapPx = Math.round(gapMm * density);
      if (gapPx > 0) {
        deadZones.push({
          x: xCursor,
          w: gapPx,
          gapMm,
          leftMonitor: prevMon.name,
          rightMonitor: mon.name,
        });
        xCursor += gapPx;
        totalWidthMm += gapMm;
      }
    }

    const sliceW = Math.round(mon.w * density);
    const sliceH = Math.round(mon.h * density);
    const sliceY = Math.round((Math.round(maxHeightMm * density) - sliceH) / 2);

    slices.push({
      monitorRef: ref,
      x: xCursor,
      y: sliceY,
      w: sliceW,
      h: sliceH,
      nativeW: mon.nw,
      nativeH: mon.nh,
      physW: mon.w,
      physH: mon.h,
      bezelTop: mon.bt,
      bezelRight: mon.br,
      bezelBottom: mon.bb,
      bezelLeft: mon.bl,
      name: mon.name,
    });

    xCursor += sliceW;
    totalWidthMm += mon.w;
  }

  const renderWidth = xCursor;
  const renderHeight = Math.round(maxHeightMm * density);

  return { totalWidthMm, maxHeightMm, density, renderWidth, renderHeight, slices, deadZones };
}
